/**
 * Interface translation (ADR-0011). Every user-visible string goes through
 * `t('ui.…')`; en/es/pt catalogs must stay complete (tested). Languages
 * without a catalog fall back to English.
 *
 * Shared strings live in packages/i18n. Feature areas may keep their own
 * strings in `src/features/<area>/i18n/{en,es,pt}.json` under a key prefix
 * unique to the area (e.g. `{"ui":{"review":{…}}}`); they are deep-merged
 * into the shared catalogs here, so parallel work streams never edit the
 * same catalog file.
 */
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import {
  CATALOGS,
  createTranslator,
  type Catalog,
  type Catalogs,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatRelativeDays,
  isRtl,
  negotiateLanguage,
} from '@sss/i18n';

export type Translate = (key: string, params?: Record<string, unknown>) => string;

export interface I18n {
  lang: string;
  dir: 'ltr' | 'rtl';
  t: Translate;
  /** Change the interface language; `remote: false` skips saving it to the user's profile. */
  setLanguage: (lang: string, opts?: { remote?: boolean }) => void;
  fmt: {
    date: (iso: string, style?: 'short' | 'medium' | 'long') => string;
    dateTime: (iso: string) => string;
    number: (n: number, digits?: number) => string;
    percent: (n: number) => string;
    currency: (n: number, currency: string) => string;
    relativeDays: (days: number) => string;
  };
}

const I18nContext = createContext<I18n | null>(null);

export function browserLanguage(): string {
  if (typeof navigator === 'undefined') return 'en';
  return negotiateLanguage(navigator.languages ?? [navigator.language ?? 'en']);
}

function deepMerge(base: Catalog, extra: Catalog): Catalog {
  const out: Catalog = { ...base };
  for (const [k, v] of Object.entries(extra)) {
    const cur = out[k];
    out[k] = typeof v === 'object' && v && typeof cur === 'object' && cur ? deepMerge(cur, v) : v;
  }
  return out;
}

const FEATURE_CATALOGS = import.meta.glob<Catalog>('../features/*/i18n/*.json', { eager: true, import: 'default' });

/** Shared catalogs + every feature catalog, merged per language. */
export const APP_CATALOGS: Catalogs = (() => {
  const merged: Catalogs = { ...CATALOGS };
  for (const [path, catalog] of Object.entries(FEATURE_CATALOGS)) {
    const lang = /\/([a-z]{2}(?:-[A-Z]{2})?)\.json$/.exec(path)?.[1];
    if (lang) merged[lang] = deepMerge(merged[lang] ?? {}, catalog);
  }
  return merged;
})();

export function makeI18n(lang: string, setLanguage: I18n['setLanguage'] = () => {}): I18n {
  const t = createTranslator(APP_CATALOGS, lang);
  return {
    lang,
    dir: isRtl(lang) ? 'rtl' : 'ltr',
    t,
    setLanguage,
    fmt: {
      date: (iso, style) => safe(() => formatDate(iso, lang, style), iso),
      dateTime: (iso) => safe(() => formatDateTime(iso, lang), iso),
      number: (n, d) => formatNumber(n, lang, d),
      percent: (n) => formatPercent(n, lang),
      currency: (n, c) => formatCurrency(n, c, lang),
      relativeDays: (d) => formatRelativeDays(d, lang),
    },
  };
}

function safe(fn: () => string, fallback: string): string {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export function I18nProvider({ lang, onChange, children }: { lang: string; onChange?: I18n['setLanguage']; children: ReactNode }) {
  const value = useMemo(() => makeI18n(lang, onChange), [lang, onChange]);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = value.dir;
  }, [lang, value.dir]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(I18nContext);
  if (!v) throw new Error('I18nProvider missing');
  return v;
}

export function useT(): Translate {
  return useI18n().t;
}
